import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  bomSelectedCategoryNameState,
  bomSelectedObject,
  materialListState,
  propertyValueOptionsState,
  selectedMaterialState,
} from "../atoms";
import { useRecoilValue } from "recoil";
import cloneDeep from "lodash.clonedeep";
import useForgeView from "./useForgeView";
import useFetchBomChildren from "./useFetchBomChildren";
import {
  extractProductPropertyValue,
  getLocationValueFromBomProperties,
  getValueFromBomProperties,
} from "../utils";
import { useAtomValue } from "jotai";
import { useScrollContext } from "../common/contexts/scroll";
import { SCROLL_REFERENCE_KEYS } from "../constants";
import { defaultBomObjectMetadata } from "../constants/bom";

const useBom = ({
  houseId,
  viewId,
  metadata,
  appliedProducts,
  productsAddedToBom,
  isBomVisible,
  isBom,
  backToCategoriesCallback
}) => {
  //refs
  const syncedObjectInBom = useRef(); // to manage that selected bom is synced in bom or not
  //states
  const [tileViewData, setTileViewData] = useState({});
  const [tableData, setTableData] = useState({});
  const [isTileView, setIsTileView] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [fetchedIds, setFetchedIds] = useState(new Set());

  //hooks
  const { loading, fetchBomChildren } = useFetchBomChildren({
    houseId,
    viewId,
  });
  const categoryName = useRecoilValue(bomSelectedCategoryNameState);
  const { getForgeParentCategoryId, getForgeParentIds } = useForgeView();
  const propertyValueOptions = useRecoilValue(propertyValueOptionsState);
  const selectedRow = useRecoilValue(bomSelectedObject);
  const materialList = useAtomValue(materialListState);
  const selectedMaterial = useAtomValue(selectedMaterialState);
  const { scrollToElement } = useScrollContext();

  const selectedRows = useMemo(() => new Set([selectedRow]), [selectedRow]);
  const selectedCategoryData = useMemo(() => {
    if (categoryName) {
      return Object.values(tileViewData).find(
        (bomObject) => bomObject.name === categoryName
      );
    }
    return null;
  }, [categoryName, tileViewData]);

  const bomContextData = useMemo(() => tableData[selectedRow] ?? selectedCategoryData, [selectedRow, tableData, selectedCategoryData]);

  const getAppliedProduct = useCallback((objectId) => {

    const bomProducts = [
      ...(Array.isArray(appliedProducts) ? appliedProducts : []),
      ...(Array.isArray(productsAddedToBom) && productsAddedToBom.length ? productsAddedToBom : []),
    ];

    return bomProducts.find((product) => product?.objectId === objectId);
  }, [appliedProducts, productsAddedToBom]);

  const getBomObjectMetadata = useCallback((objectid) => metadata?.find((data) => data.objectid === objectid) || defaultBomObjectMetadata, [metadata]);

  const generateBomData = useCallback((object) => {
    const { objectid, items, properties = [], leafNode, level } = object;

    const bomProduct = getAppliedProduct(objectid);
    const appliedProductProperties = extractProductPropertyValue(propertyValueOptions, bomProduct?.product);

    const carbonEmissionType = (appliedProductProperties?.gwp) ? "gwp" : "material";
    const materials = materialList.filter(sandboxMaterialData => sandboxMaterialData.objectIds.includes(objectid));
    const hasMaterial = !!materials.length;
    const isMaterialSelected = materials.find((materialData) => materialData.id === selectedMaterial);
    const isCompoundMaterialApplied = materials.every(materialData => materialData.isCompoudLayerMaterial);
    const discipline = Array.isArray(appliedProductProperties?.disciplines) ? appliedProductProperties.disciplines.join(", ") : getValueFromBomProperties(properties, "discipline", "");
    const className = `${isMaterialSelected ? "material-applied" : ""}`

    const data = {
      id: objectid,
      rawData: object,
      bomProduct,
      items,
      leafNode,
      level,
      carbonEmissionType,
      thickness: getValueFromBomProperties(properties, "thickness", ""),
      area: getValueFromBomProperties(properties, "area", ""),
      volume: getValueFromBomProperties(properties, "volume", ""),
      division: getValueFromBomProperties(properties, "division", ""),
      location: leafNode ? getLocationValueFromBomProperties(properties) : "",
      discipline,
      selected: selectedRow === objectid,
      isCompoundMaterialApplied, // If material exists then is it compoun layer material
      materials, // material related data
      hasMaterial, // any material exists on row or not
      isMaterialSelected,
      className,
      rowNotSelectable: (leafNode && (level !== 2)),
      ...getBomObjectMetadata(objectid)
    };

    return data;
  }, [propertyValueOptions, selectedMaterial, selectedRow, materialList, getAppliedProduct, getBomObjectMetadata]);

  const getRecursiveChildRows = useCallback((parentId) => {
    // generateBomData
    return Object.values(tableData).filter((object) => object.parentId === parentId).map((object) => {
      const copyObject = generateBomData(cloneDeep(object));

      if (!copyObject.leafNode) {
        const childrens = getRecursiveChildRows(copyObject.rawData.id);
        copyObject.childrens = childrens;
      }

      return copyObject;
    });
  }, [tableData, generateBomData]);

  const loadInitialTableData = useCallback(async (categoryId) => {
    try {
      setIsTileView(false);
      setFetchedIds(new Set([categoryId]));
      setExpandedRows(new Set([categoryId]));

      const objects = await fetchBomChildren({ objectId: categoryId, withLoader: true })
      // const data = objects.map((object) => generateBomData(object));

      setTableData(objects.reduce((oldObjects, newObject) => ({ ...oldObjects, [newObject.objectid]: newObject, }), {}));

      return objects;
    } catch (error) {
      console.log(error)
    }
  }, [fetchBomChildren]);

  const backToCategories = useCallback(() => {
    setIsTileView(true);
    backToCategoriesCallback()
  }, [backToCategoriesCallback]);

  const sortData = useCallback(() => { }, []);

  //fetch row data
  const fetchRowData = useCallback(async (objectId, withLoader = false) => {
    try {
      if (fetchedIds.has(objectId)) {
        return;
      }

      const newBomObjects = await fetchBomChildren({ objectId, withLoader })
      setTableData((oldState) => ({
        ...oldState, ...newBomObjects.reduce((oldObjects, newObject) => ({ ...oldObjects, [newObject.objectid]: newObject, }), {}),
      }))

    } catch (error) {
      console.log(error);
    } finally {
      setFetchedIds((oldState) => new Set([...oldState, objectId]));
    }
  }, [fetchedIds, fetchBomChildren]);

  //when row is open or closed
  const openRowAsync = useCallback(async (object) => {
    await fetchRowData(object.id);
  }, [fetchRowData]);

  const onToggleRowExpansion = useCallback((id, isOpening) => {
    if (!isOpening && expandedRows.has(id)) {
      setExpandedRows((oldExpandedIds) => {
        const set = new Set(oldExpandedIds);
        set.delete(id);
        return set;
      });
    } else if (isOpening && !expandedRows.has(id)) {
      setExpandedRows(
        (oldExpandedIds) => new Set([...oldExpandedIds, id])
      );
    }
  }, [expandedRows])

  const columnData = useMemo(() => generateBomData(selectedCategoryData ?? {}), [selectedCategoryData, generateBomData]);

  //generate rows data in tree formate
  const rowData = useMemo(() => {
    return selectedCategoryData?.id ? getRecursiveChildRows(selectedCategoryData.id) : [];
  }, [getRecursiveChildRows, selectedCategoryData]);

  //effects

  // keep the cached table row in sync when a BOM object is edited elsewhere
  // (e.g. property edits saved from the details panel)
  const updateBomObjectInCache = useCallback((updatedObject) => {
    const objectid = updatedObject?.objectid;
    if (!objectid) return;
    setTableData((oldState) => (
      oldState[objectid] ? { ...oldState, [objectid]: { ...oldState[objectid], ...updatedObject } } : oldState
    ));
  }, [])

  const initializeBom = useCallback(async () => {
    try {
      const categoriesData = await fetchBomChildren({ objectId: "ROOT", withLoader: true })
      setTileViewData(categoriesData)
    } catch (error) {
      console.log(error);
    }
  }, [fetchBomChildren])

  //load initial data
  useEffect(() => {
    if (isBom) {
      initializeBom();
    }
  }, [isBom, initializeBom])

  useEffect(() => {
    const openSelectedObjectInBom = async (objectId) => {
      if (syncedObjectInBom.current !== objectId && !loading && isBomVisible) {
        syncedObjectInBom.current = objectId;
        const parentIds = getForgeParentIds(objectId);
        const categoryId = getForgeParentCategoryId(objectId);
        const isCategoryAlreadyOpen = fetchedIds.has(categoryId); // is already in category's table view

        if (!categoryId) {
          return;
        }

        if (!isCategoryAlreadyOpen) {
          //If category getting open for the first time...
          await loadInitialTableData(categoryId);
        }

        const shouldOpenRowIds = parentIds.filter((id) => id !== categoryId);
        if (!shouldOpenRowIds.length) {
          return;
        }

        for (const id of shouldOpenRowIds) {
          if (!expandedRows.has(id)) {
            setExpandedRows(
              (oldExpandedIds) => new Set([...oldExpandedIds, id])
            );
            await fetchRowData(id, !isCategoryAlreadyOpen); //show loading only if category is loaded first time
          }
        }
        scrollToElement(SCROLL_REFERENCE_KEYS.bomTable, `[data-key="${objectId}"]`, 88)
      }
    };

    setIsTileView(!selectedRow);
    if (selectedRow) {
      openSelectedObjectInBom(selectedRow);
    }
  }, [
    isBomVisible,
    selectedRow,
    expandedRows,
    loading,
    fetchedIds,
    getForgeParentIds,
    fetchRowData,
    getForgeParentCategoryId,
    loadInitialTableData,
    scrollToElement
  ]);

  return {
    loading,
    isTileView,
    tileViewData,
    selectedCategoryData,
    expandedRows,
    columnData,
    rowData,
    selectedRows,
    bomContextData,
    backToCategories,
    getBomObjectMetadata,
    sortData,
    openRowAsync,
    getAppliedProduct,
    onToggleRowExpansion,
    loadInitialTableData,
    updateBomObjectInCache
  };
};

export default useBom;
